import React, { useState } from 'react';
import {
  withStyles,
  Modal,
} from '@material-ui/core';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import {
  deleteRefuelerItem,
  setRefuelerValues,
  restoreOriginalRefueler,
  createRefuelerItem,
  updateRefuelerList,
  searchRefuelerList,
} from '~/store/dailyparts/actions';
import { useTranslation } from 'react-i18next';
import styles from '../styles';
import EditHistoryModal from '~/components/EditHistoryModal';
import {
  formatDate, formatTime, newMomentDate, parseDate, parseDateTimeToTimestamp, subtractDaysFromNow,
} from '~/utils/moment';
import PF2MDataTable from '~/components/PF2MDataTable';
import EditHistoryButton from '~/components/EditHistoryButton';
import usePlatformConfiguration, { usePlatformConfigurationUserAccessLevel } from '~/services/platformConfiguration';
import { usePagination } from '../hooks';

const REFUELER_ID = 14;
const RefuelerList = () => {
  const { t: translate } = useTranslation();
  const refuelerList = useSelector(state => state.dailyParts.refuelerList);
  const filters = useSelector(state => state.dailyParts.filters);
  const refuelerGroups = useSelector(state => state.manager.equipmentsGroups
    .filter(e => e.id_equipament === REFUELER_ID), shallowEqual);
  const refuelers = useSelector(state => state.manager.equipments
    .filter(e => e.id_equip === REFUELER_ID), shallowEqual);
  const operatorGroups = useSelector(state => state.manager.operatorsGroups
    .filter(o => o.name), shallowEqual);
  const operators = useSelector(state => state.manager.operators.filter(o => o.name), shallowEqual);
  const equipmentTypes = useSelector(state => state.manager.equipmentTypes);
  const equipmentsGroups = useSelector(state => state.manager.equipmentsGroups);
  const equipments = useSelector(state => state.manager.equipments);
  const exceptionTypes = useSelector(state => state.manager.exceptionTypes);
  const elements = useSelector(state => state.manager.elements);
  const subElements = useSelector(state => state.manager.subElements);
  const originalRefueler = useSelector(state => state.dailyParts.originalRefuelerList);
  const refuelerPagination = useSelector(
    state => state.dailyParts.pagination.refuelerPagination,
  );

  const userAccessLevel = useSelector(state => state.auth.user.level);
  const { value: userLimitEditConfig } = usePlatformConfigurationUserAccessLevel(userAccessLevel);
  const closures = useSelector(state => state.manager.closures);
  const { value: enableClosure } = usePlatformConfiguration('enable_closure');

  const [openEditHistoryModal, setOpenEditHistoryModal] = useState(false);
  const [selectedEditHistoryData, setSelectedEditHistoryData] = useState(null);

  const dispatch = useDispatch();

  const customEditHistoryColumns = [
    {
      title: translate('common:StartDate'),
      field: 'start_date',
    },
    {
      title: translate('common:InitialTime'),
      field: 'start_time',
    },
    {
      title: translate('common:EndDate'),
      field: 'end_date',
    },
    {
      title: translate('common:EndTime'),
      field: 'end_time',
    },
    {
      title: translate('common:OperatorTeam'),
      field: 'operator_group_id',
      lookup: operatorGroups,
    },
    {
      title: translate('common:RefuelOperatorRegistration'),
      field: 'operator_id',
      lookup: operators,
      lookupKey: 'id_operator',
    },
    {
      title: translate('common:RefuelEquipmentGroup'),
      field: 'equip_group_id',
      lookup: equipmentsGroups,
    },
    {
      title: translate('common:RefuelEquipment'),
      field: 'equip_id',
      lookup: equipments,
    },
    {
      title: translate('common:RefueledEquipmentType'),
      field: 'equip_type_fueled',
      lookup: equipmentTypes,
    },
    {
      title: translate('common:RefueledEquipmentGroup'),
      field: 'equip_group_id_fueled',
      lookup: equipmentsGroups,
    },
    {
      title: translate('common:RefueledEquipment'),
      field: 'equip_id_fueled',
      lookup: equipments,
    },
    {
      title: translate('common:RefueledOperatorGroup'),
      field: 'operator_group_id_fueled',
      lookup: operatorGroups,
    },
    {
      title: translate('common:RefueledOperator'),
      field: 'operator_id_fueled',
      lookup: operators,
      lookupKey: 'id_operator',
    },
    {
      title: translate('common:CycleType'),
      field: 'exception_type',
      lookup: exceptionTypes,
    },
  ];

  const isDateEditable = (rowData, key) => {
    let userLimitEdit = subtractDaysFromNow(userLimitEditConfig);

    if (enableClosure && closures?.length > 0) {
      const dateNow = newMomentDate();
      const lastClosureDate = parseDate(closures[0].timestampClose, true);
      const currentDaysFromLastClosure = dateNow.diff(lastClosureDate, 'days');

      const lockEditConfig = subtractDaysFromNow(userLimitEditConfig);
      const lockEditLastClosure = subtractDaysFromNow(currentDaysFromLastClosure);

      userLimitEdit = currentDaysFromLastClosure > userLimitEditConfig
        ? lockEditConfig
        : lockEditLastClosure.add(1, 'day');
    }

    if (rowData[key] < userLimitEdit) {
      return { isValid: false, helperText: translate('validation:InvalidDate') };
    }

    const currentDatetime = newMomentDate();
    const stDate = rowData.start_date || currentDatetime.format('DD/MM/YYYY');
    const stTime = rowData.start_time || currentDatetime.format('HH:mm:ss');
    const endDate = rowData.end_date || currentDatetime.format('DD/MM/YYYY');
    const endTime = rowData.end_time || currentDatetime.format('HH:mm:ss');

    const startDateTimestamp = parseDateTimeToTimestamp(stDate, stTime);
    const endDateTimestamp = parseDateTimeToTimestamp(endDate, endTime);

    if (key.startsWith('end_')) {
      if (endDateTimestamp < startDateTimestamp) {
        return {
          isValid: false,
          helperText: translate('validation:FinalDateSmallerThanInitialDate'),
        };
      }
      if (endDateTimestamp > currentDatetime.unix() * 1000) {
        return {
          isValid: false,
          helperText: translate('validation:FutureDateCantBeSelected'),
        };
      }
    }

    return { isValid: true };
  };

  const columns = [
    {
      title: 'ID',
      editable: 'never',
      field: 'id',
    },
    {
      editable: 'never',
      title: translate('common:EditHistory'),
      field: 'edit_history',
      render: row => (
        <EditHistoryButton
          exceptionType={row.exception_type}
          onClick={() => {
            setOpenEditHistoryModal(true);
            setSelectedEditHistoryData(row);
          }}
        />
      ),
    },
    {
      title: translate('common:StartDate'),
      field: 'start_date',
      customType: 'date',
      validate: rowData => isDateEditable(rowData, 'start_date'),
    },
    {
      title: translate('common:InitialTime'),
      field: 'start_time',
      customType: 'time',
    },
    {
      title: translate('common:EndDate'),
      field: 'end_date',
      customType: 'date',
      validate: rowData => isDateEditable(rowData, 'end_date'),
    },
    {
      title: translate('common:EndTime'),
      field: 'end_time',
      customType: 'time',
    },
    {
      title: translate('common:OperatorTeam'),
      field: 'operator_group_id',
      lookup: operatorGroups,
      customType: 'filterLookup',
      customOnChange: () => ({
        operator_id: '',
      }),
    },
    {
      title: translate('common:RefuelOperatorRegistration'),
      field: 'operator_id',
      lookup: operators,
      lookupKey: 'id_operator',
      customType: 'filterLookup',
      lookupFilter: (opt, row) => String(opt.id_group) === String(row.operator_group_id),
    },
    {
      title: translate('common:RefuelEquipmentGroup'),
      field: 'equip_group_id',
      lookup: refuelerGroups,
      customType: 'filterLookup',
      customOnChange: () => ({
        equip_id: '',
      }),
    },
    {
      title: translate('common:RefuelEquipment'),
      field: 'equip_id',
      lookup: refuelers,
      customType: 'filterLookup',
      lookupFilter: (opt, row) => String(opt.id_group) === String(row.equip_group_id),
    },
    {
      title: translate('common:RefueledEquipmentType'),
      field: 'equip_type_fueled',
      lookup: equipmentTypes,
      customType: 'filterLookup',
      customOnChange: () => ({
        equip_group_id_fueled: '',
        equip_id_fueled: '',
      }),
    },
    {
      title: translate('common:RefueledEquipmentGroup'),
      field: 'equip_group_id_fueled',
      lookup: equipmentsGroups,
      customType: 'filterLookup',
      lookupFilter: (opt, row) => String(opt.id_equipament) === String(row.equip_type_fueled),
      customOnChange: () => ({
        equip_id_fueled: '',
      }),
    },
    {
      title: translate('common:RefueledEquipment'),
      field: 'equip_id_fueled',
      lookup: equipments,
      customType: 'filterLookup',
      lookupFilter: (opt, row) => String(opt.id_group) === String(row.equip_group_id_fueled),
    },
    {
      title: translate('common:RefueledOperatorGroup'),
      field: 'operator_group_id_fueled',
      lookup: operatorGroups,
      customType: 'filterLookup',
      customOnChange: () => ({
        operator_id_fueled: '',
      }),
    },
    {
      title: translate('common:RefueledOperator'),
      field: 'operator_id_fueled',
      lookup: operators,
      lookupKey: 'id_operator',
      customType: 'filterLookup',
      lookupFilter: (opt, row) => String(opt.id_group) === String(row.operator_group_id_fueled),
    },
    { title: translate('common:RefuelInitialLatitude'), field: 'latitude_start', customType: 'number' },
    { title: translate('common:RefuelInitialLongitude'), field: 'longitude_start', customType: 'number' },
    {
      title: translate('common:RefuelInitialElement'),
      field: 'element_id_start',
      customType: 'filterLookup',
      lookup: elements,
      customOnChange: () => ({
        element_point_id_start: '0',
      }),
    },
    {
      title: translate('common:RefuelInitialSubElement'),
      field: 'element_point_id_start',
      customType: 'filterLookup',
      lookup: subElements,
      lookupFilter: (opt, row) => String(opt.id_element) === String(row.element_id_start),
    },
    {
      title: translate('common:RefuelFinalElement'),
      field: 'element_id_end',
      customType: 'filterLookup',
      lookup: elements,
      customOnChange: () => ({
        element_point_id_end: '0',
      }),
    },
    { title: translate('common:RefuelFinalLatitude'), field: 'latitude_end', customType: 'number' },
    { title: translate('common:RefuelFinalLongitude'), field: 'longitude_end', customType: 'number' },
    {
      title: translate('common:RefuelFinalSubElement'),
      field: 'element_point_id_end',
      customType: 'filterLookup',
      lookup: subElements,
      lookupFilter: (opt, row) => String(opt.id_element) === String(row.element_id_end),
    },
    { title: translate('common:RefueledHourmeterEquipment'), field: 'hourmeter_fueled', customType: 'number' },
    { title: translate('common:RefueledEquipmentKilometer'), field: 'kilometer_fueled', customType: 'number' },
    { title: translate('common:RefueledLiters'), field: 'liters_fueled', customType: 'number' },
    { title: translate('common:StopOperationTime'), field: 'code_time', customType: 'number' },
    { title: translate('common:RefuelTime'), field: 'refuel_time', customType: 'number' },
    { title: translate('common:RefuelIntervalTime'), field: 'waiting_time', customType: 'number' },
    { title: translate('common:RefuelIntervalDistance'), field: 'refuel_distance', customType: 'number' },
    {
      title: translate('common:CycleType'),
      field: 'exception_type',
      lookup: exceptionTypes,
      editable: 'never',
    },
  ];

  const filterToFormFieldMap = {
    operatorGroup: 'operator_group_id',
    operator: 'operator_id',
    equipmentGroup: 'equip_group_id',
    equipment: 'equip_id',
  };

  const convertIfNeeded = (value, key) => {
    if (key === 'codeType' || key === 'codeGroup') {
      return Number(value);
    }
    return value;
  };

  const mappedFilterData = Object.entries(filterToFormFieldMap).reduce(
    (acc, [filterKey, formField]) => {
      const filterValue = filters[filterKey];

      if (filterValue !== undefined && filterValue !== 0) {
        if (Array.isArray(filterValue) && filterValue.length === 1) {
          const [firstValue] = filterValue;
          if (firstValue !== undefined) {
            acc[formField] = convertIfNeeded(firstValue, filterKey);
          }
        } else {
          acc[formField] = convertIfNeeded(filterValue, filterKey);
        }
      }

      return acc;
    },
    {},
  );

  // Verificar campos obrigatórios para desabilitar o botão de salvar
  const disableSaveAllChangesButton = refuelerList?.length ? refuelerList
    .filter(row => row.touched)
    .flatMap(row => (
      Object.entries(row).map(([key, value]) => columns.find(col => col.field === key)?.required
      && value === '')
    ))
    .includes(true) : false;

  const initialFormData = {
    ...mappedFilterData,
    start_date: formatDate(newMomentDate(), true),
    end_date: formatTime(newMomentDate(), 'DD/MM/YYYY'),
    start_time: formatTime(newMomentDate(), 'HH:mm:ss'),
    end_time: formatTime(newMomentDate(), 'HH:mm:ss'),
    turn_end: 0,
    operator_group_id: 0,
    operator_id: 0,
    equip_group_id: 0,
    equip_id: 0,
    equip_type_fueled: 0,
    equip_group_id_fueled: 0,
    equip_id_fueled: 0,
    operator_group_id_fueled: 0,
    operator_id_fueled: 0,
    latitude_start: 0,
    longitude_start: 0,
    element_id_start: 0,
    element_point_id_start: 0,
    element_id_end: 0,
    latitude_end: 0,
    longitude_end: 0,
    element_point_id_end: 0,
    hourmeter_fueled: 0,
    kilometer_fueled: 0,
    liters_fueled: 0,
    code_time: '0',
    refuel_time: '0',
    waiting_time: '0',
    refuel_distance: 0,
  };

  const removeUnnecessaryFields = (data) => {
    const result = { ...data };
    const fieldsToRemove = [
      'startDate',
      'endDate',
      'code',
      'equipmentType',
      'operatorGroup',
      'operator',
      'startOrigin',
      'endOrigin',
      'startSubOrigin',
      'endSubOrigin',
      'codeType',
      'codeGroup',
      'searchDailyParts',
      'hideInterruptedOrDeleted',
      'showOnlyReservedEquips',
      'equipment',
      'equipmentGroup',
      'touched',
    ];

    fieldsToRemove.forEach((field) => {
      delete result[field];
    });

    return result;
  };

  const { applyDefaultSort, ...paginationOptions } = usePagination({
    filters,
    dispatch,
    listType: 'refueler',
    pagination: refuelerPagination,
    searchAction: searchRefuelerList,
  });

  const { page = 0, total = 0, loadingMore = false } = refuelerPagination || { };

  return (
    <>
      <PF2MDataTable
        title={translate('common:Cycles')}
        page={page}
        totalCount={total}
        isLoading={loadingMore}
        columns={applyDefaultSort(columns)}
        data={refuelerList}
        initialFormData={initialFormData}
        options={{
          pageSize: 10,
          pageSizeOptions: [10, 20, 50, 100],
          showTitle: true,
          selection: true,
          search: true,
          cacheKey: 'refueler_list',
          maxBodyHeight: 'calc(100vh - 290px)',
          minBodyHeight: 'calc(100vh - 290px)',
          disableSaveButton: disableSaveAllChangesButton,
          showFirstLastPageButtons: false,
          ...paginationOptions,
        }}
        onAdd={(newItem) => {
          const createParams = {
            ...newItem,
            ...mappedFilterData,
          };
          dispatch(createRefuelerItem(createParams));
        }}
        onDiscard={() => dispatch(restoreOriginalRefueler())}
        onApply={() => {
          const parsedData = refuelerList
            .filter(item => item.update_status)
            .map(removeUnnecessaryFields);
          dispatch(
            updateRefuelerList({
              data: parsedData,
              parameters: filters,
              saveLog: true,
              ogData: originalRefueler,
            }),
          );
        }}
        onChange={(newData) => {
          newData.forEach((row) => {
            dispatch(setRefuelerValues(row, row));
          });
        }}
        onDelete={(newData) => {
          newData.forEach((e) => {
            dispatch(deleteRefuelerItem(e));
          });
        }}
      />
      <Modal
        open={openEditHistoryModal}
        onClose={() => setOpenEditHistoryModal(false)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <EditHistoryModal
          listName="refueler"
          referenceColumns={columns}
          customEditHistoryColumns={customEditHistoryColumns}
          selectedEditHistoryData={selectedEditHistoryData}
          handleClose={() => setOpenEditHistoryModal(false)}
        />
      </Modal>
    </>
  );
};

RefuelerList.propTypes = {};

RefuelerList.defaultProps = {};

export default withStyles(styles)(RefuelerList);
